// A pipeline stage as iOS draws it on cards (`Chip(text: stage.name, color: Theme.color(hex: stage.colorHex))`): the
// stage color at 13 % behind the name in a readable shade of the same color.

import type { StagePayload } from '@/contracts/models';
import { PIN_GRAY } from '@/domain/records';
import { Chip, type ChipSize } from '@/ui';

export interface StageChipProps {
  /** The record's stage (`stageFor(record, pipelines)`); missing renders `emptyLabel`, or nothing. */
  stage: Pick<StagePayload, 'name' | 'colorHex'> | null | undefined;
  size?: ChipSize;
  /** Solid fill (selected column headers, legends). */
  filled?: boolean;
  /** Shown as a gray chip when there is no stage ("No stage" in tables); null (default) renders nothing, as iOS. */
  emptyLabel?: string | null;
  className?: string;
}

export function StageChip({ stage, size = 'sm', filled = false, emptyLabel = null, className }: StageChipProps) {
  if (!stage) {
    if (emptyLabel === null) return null;
    return (
      <Chip color={PIN_GRAY} size={size} className={className}>
        {emptyLabel}
      </Chip>
    );
  }
  const name = stage.name.trim() === '' ? 'Untitled stage' : stage.name;
  return (
    <Chip color={stage.colorHex} filled={filled} size={size} title={`Stage: ${name}`} className={className}>
      <span className="sr-only">Stage </span>
      {name}
    </Chip>
  );
}
